import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';
import type { MandatCommissaireAuxComptesCreateRequest } from '../../../../performance/cac/models/cac-api.model';
import type { MandatDetailleCac, PersonneMoraleRef, RattachementCac, RemplacementCac } from '../../../../performance/cac/models/cac.model';

// Ré-export des types de contrat CAC : les consommateurs Gouvernance passent par ce port
// et n'importent jamais directement le module Performance. Imports `type` uniquement →
// effacés à la compilation, donc AUCUNE dépendance runtime Gouvernance → Performance.
export type { MandatCommissaireAuxComptesCreateRequest, MandatDetailleCac, PersonneMoraleRef, RattachementCac, RemplacementCac };

/** Filtre de lecture des rattachements CAC (sous-ensemble consommé par la Fiche 360°). */
export type CacRattachementQuery = {
    societe?: string;
    per_page?: number;
    /** Mandats en cours seulement (`date_fin__gte`, format API) — appliqué par le serveur. */
    date_fin__gte?: string;
};

/**
 * Port (hexagonal) exposant au module Gouvernance les seules opérations CAC dont la
 * Fiche 360° a besoin, sans dépendre des services concrets du module Performance.
 * L'implémentation (adaptateur) vit dans `performance/cac` et est câblée au composition
 * root (`app.config`) via le token `CAC_PROVIDER`.
 */
/** Reconduction d'un mandat : ce que l'agent saisit, le reste étant repris du mandat d'origine. */
export type RenouvellementCac = {
    /** Mandat reconduit — c'est lui que le serveur relit pour répliquer la paire. */
    mandat_id: string;
    /** Date de début du nouveau mandat, au format API `AAAA-MM-JJ`. */
    date_debut_mandat: string;
    /** Référence de l'acte qui porte la reconduction ; `null` si elle n'est pas connue. */
    reference_acte_nomination: string | null;
};

export type CacProvider = {
    listCabinets(): Observable<PersonneMoraleRef[]>;
    listRattachements(query: CacRattachementQuery): Observable<{ results: RattachementCac[] }>;
    /** Désignation : un acte, un titulaire, son suppléant éventuel — le mandat porte la paire. */
    createMandat(payload: MandatCommissaireAuxComptesCreateRequest): Observable<{ id: string }>;

    /**
     * Remplacement d'un rôle en cours de mandat : la personne qui succède, sa prise d'effet et le
     * motif. Le champ écrit dépend du rôle — le mandat porte le titulaire et le suppléant.
     */
    remplacerCommissaire(payload: RemplacementCac): Observable<unknown>;

    /**
     * Renouvellement : un nouveau mandat reprend la paire du précédent — titulaire, suppléant,
     * cabinets et natures — avec la date de début saisie. Le serveur recalcule durée, période et
     * rang, et refuse si la limite de mandats du porteur est atteinte.
     */
    renouvelerMandat(payload: RenouvellementCac): Observable<unknown>;
    /** Mandats CAC portés par une personne physique (état de service de la fiche personne). */
    listMandatsPersonne(personneId: string): Observable<MandatDetailleCac[]>;
};

/** Jeton DI du port CAC — fourni par `CacProviderAdapter` (module Performance) au composition root. */
export const CAC_PROVIDER = new InjectionToken<CacProvider>('CAC_PROVIDER');
